import React, { useEffect, useState, memo, useCallback, useRef } from "react";
import { className } from "utils";
import { ReactHlsPlayer } from "components/UI/HlsPlayer";
import { ReactComponent as PlayIcon } from "components/icons/play_icon.svg";
import { apiMain } from "services";
import { useDispatch } from "react-redux";
import { toggleGifSound } from "store/application/application-action";
import { playbackTracker } from "utils/playback";
import { LikeOverlay } from "./LikeOverlay";
import { TapTracker } from "components/UI/TapTracker";
import "./VideoComponent.scss";

interface VideoComponentProps {
  video: string;
  sound: boolean;
  poster: string;
  canPlay: boolean;
  fullscreen: boolean;
  onPlay: () => void;
  onError?: (error: number, resetError: () => void) => void;
  isHls: boolean;
  gifId: string;
  videoRef: React.RefObject<HTMLVideoElement>;
  playing: boolean;
  handleLoadedMetadata: () => void;
  handleTimeUpdate: () => void;
  handleLike: () => void;
}

export const VideoComponent = (props: VideoComponentProps) => {
  const {
    video,
    sound,
    poster,
    canPlay,
    fullscreen,
    onPlay,
    onError,
    videoRef,
    playing,
    handleLoadedMetadata,
    handleTimeUpdate,
    gifId,
    handleLike
  } = props;
  const dispatch = useDispatch();

  const startLoadTimeRef = useRef(0);
  const canPlayTimeRef = useRef(0);
  const playPromiseRef = useRef<Promise<void>>();

  const [paused, setPaused] = useState(false);
  const [error, setError] = useState<string|null>(null);
  const [isTriedToPlay, setIsTriedToPlay] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Stop playback on tab change.
  useEffect(() => {
    const handler = () => {
      if (document.hidden) {
        if (videoRef.current) {
          videoRef.current.pause();
          setPaused(true);
          playbackTracker.stop();
        }
      }
    };

    document.addEventListener("visibilitychange", handler);

    return () => {
      document.removeEventListener("visibilitychange", handler);
    };
  }, [videoRef]);

  useEffect(() => {
    const listener = () => {
      playbackTracker.stop();
    };

    window.addEventListener("beforeunload", listener);

    return () => {
      window.removeEventListener("beforeunload", listener);
    };
  }, []);

  const handlePlayError = useCallback((e: any) => {
    console.warn("failed to play video", e);
    dispatch(toggleGifSound(false));

    setTimeout(() => {
      videoRef.current?.play().then(() => {
        setPaused(false);
        onPlay();
        playbackTracker.start(gifId);
      }).catch(e => {
        console.error("Failed to play video on retry", e);
        setPaused(true);
        setIsTriedToPlay(true);
        playbackTracker.stop();
      });
    // eslint-disable-next-line no-magic-numbers
    }, 1000);
  }, [dispatch, gifId, onPlay, videoRef]);

  const play = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    try {
      playPromiseRef.current = videoRef.current.play();

      if (playPromiseRef.current !== undefined) {
        playPromiseRef.current.then(() => {
          setPaused(false);
          onPlay();
          playbackTracker.start(gifId);
        }).catch(handlePlayError).finally(() => {
          setIsTriedToPlay(true);
        });
      } else {
        setPaused(false);
        setIsTriedToPlay(true);
        onPlay();
        playbackTracker.start(gifId);
      }
    } catch (e) {
      handlePlayError(e);
    }
  }, [onPlay, gifId, videoRef, handlePlayError]);

  const pause = useCallback(() => {
    if (playPromiseRef.current !== undefined) {
      playPromiseRef.current.then(() => {
        if (!videoRef.current) {
          return;
        }

        playbackTracker.stop();
        videoRef.current.pause();
        setPaused(true);
      });
    } else {
      if (!videoRef.current) {
        return;
      }

      playbackTracker.stop();
      videoRef.current.pause();
      setPaused(true);
    }
  }, [videoRef]);

  useEffect(() => {
    if (!videoRef.current || error) {
      return;
    }

    if (canPlay && videoRef.current.paused && playing) {
      // fix IOS issue with stop playing video in FS
      if (
        navigator.platform === "iPhone" && fullscreen
      ) {
        setTimeout(play, 1);
        return;
      }

      play();
      return;
    }

    if (!playing || !videoRef.current.paused) {
      pause();
      return;
    }

    if (!canPlay && !videoRef.current.paused) {
      pause();
    }
  }, [canPlay, play, pause, error, video, videoRef, playing, fullscreen]);

  const handleClick = useCallback(() => {
    if (!videoRef.current || error) {
      return;
    }

    videoRef.current.paused ? play() : pause();
  }, [error, play, pause, videoRef]);

  const handleError = useCallback(() => {
    const v = videoRef.current;

    if (v) {
      setError(v.networkState === v.NETWORK_NO_SOURCE ? "Error downloading the video file." : "Unknown error.");
      if (onError) {
        onError(v.networkState, () => {
          setError(null);
        });
      }
    } else {
      console.error("Video playback failed, but the ref is not yet ready.");
    }
  }, [videoRef, onError]);

  const handleLoadedData = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handleLoadStart = useCallback(() => {
    startLoadTimeRef.current = performance.now();
  }, []);

  const handleCanPlay = useCallback(() => {
    if (canPlayTimeRef.current === 0) {
      canPlayTimeRef.current = performance.now();
      const duration = Math.ceil(canPlayTimeRef.current - startLoadTimeRef.current);

      console.debug(
        "loaded time(ms):",
        duration,
        props.video,
        "isHls:",
        props.isHls
      );

      apiMain.sendVideoLoaded({
        duration,
        isHLS: props.isHls
      });
    }
  }, [props.video, props.isHls]);

  const preload = fullscreen ? "none" : "metadata";

  const handleTapAction = useCallback((clicks: number) => {
    if (clicks === 1) {
      handleClick();
    }

    if (fullscreen && clicks === 2) {
      handleLike();
    }
  }, [handleClick, handleLike, fullscreen]);

  return (
    <TapTracker onTap={handleTapAction}>
      <div className="videoWrapper">
        {
          !isLoaded && (
            <img src={poster} alt=""/>
          )
        }

        <ReactHlsPlayer
          className={className({
            error: !!error,
            isLoaded: isLoaded,
          })}
          isHls={props.isHls}
          controls={false}
          autoPlay={false}
          loop
          playsInline
          preload={preload}
          onCanPlay={handleCanPlay}
          onError={handleError}
          onLoadStart={handleLoadStart}
          onLoadedData={handleLoadedData}
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          muted={!sound}
          playerRef={videoRef}
          disablePictureInPicture={true}
          src={video}
        />

        {paused && isTriedToPlay && !error && (
          <PlayIcon
            className="playButton"
          />
        )}

        {fullscreen && (
          <>
            <LikeOverlay isLiked />
            <LikeOverlay />
          </>
        )}

        {error && (
          <p className="error">{error}</p>
        )}
      </div>
    </TapTracker>
  );
};

export const VideoComponentMemo = memo(VideoComponent);
